import { GetProposalQuery, ProposalSelectionMode } from '@/gql/graphql';
import { produce } from 'immer';

type Section = GetProposalQuery['proposal']['sections'][number];
type ItemGroupSection = Extract<
  Section,
  { __typename: 'ProposalItemGroupSection' }
>;
type Group = ItemGroupSection['groups'][number];
type GroupChild = Group['children'][number];
type SubGroup = Extract<GroupChild, { __typename: 'ProposalItemGroup' }>;
type LeafChild = SubGroup['children'][number];

/** A child of a group in the proposal tree: either a direct child of a
 * top-level group, or a child of a sub-group one level deeper. */
export type ProposalGroupChild = GroupChild | LeafChild;

/**
 * Whether `id` addresses this line item. Mutation inputs may carry either the
 * numeric id or the public UUID — an optimistically created row only exists
 * under the latter — so optimistic matching accepts both forms.
 */
export const isProposalLineItemId = (
  proposalLineItem: { id: string; publicId: string },
  id: string
): boolean => proposalLineItem.id === id || proposalLineItem.publicId === id;

/**
 * Immutably walk a cached proposal, invoking `visit` once per group with that
 * group's direct children and selection mode. Covers top-level groups in every
 * item-group section and their sub-groups — the two levels of nesting the
 * proposal builder supports. Centralises that nesting assumption so optimistic
 * updates can target a child wherever it lives without re-implementing the walk.
 */
export const produceProposalGroupChildren = (
  data: GetProposalQuery,
  visit: (
    children: ProposalGroupChild[],
    selectionMode: ProposalSelectionMode
  ) => void
): GetProposalQuery =>
  produce(data, (draft) => {
    for (const section of draft.proposal.sections) {
      if (section.__typename !== 'ProposalItemGroupSection') continue;
      for (const group of section.groups) {
        visit(group.children, group.selectionMode);
        for (const child of group.children) {
          if (child.__typename === 'ProposalItemGroup') {
            visit(child.children, child.selectionMode);
          }
        }
      }
    }
  });

/**
 * Mutate a group's direct children in place to mirror the server's
 * single-winner rule for MULTI_CHOICE groups (`enforceSingleMultiChoiceWinner`
 * in the proposal content logic): a non-empty MULTI_CHOICE group carries
 * exactly one selected child — an existing winner is kept, otherwise the first
 * child wins. A no-op for other selection modes.
 */
export const enforceSingleMultiChoiceWinner = (
  children: ProposalGroupChild[],
  selectionMode: ProposalSelectionMode
): void => {
  if (selectionMode !== 'MULTI_CHOICE' || children.length === 0) return;
  const winnerIndex = Math.max(
    children.findIndex((child) => 'selected' in child && child.selected),
    0
  );
  children.forEach((child, index) => {
    if ('selected' in child) child.selected = index === winnerIndex;
  });
};
